// Action Queue redesign — proposal detail drawer.
//
// Slides in from the right when a triage row is opened. Shows the full
// context the row omits: account / entity / id, a hero "change" block,
// the reasoning, and who proposed it. The footer is the action surface —
// approve (with an optional note) or deny (reason required, >= 5 chars).
// Ported from the design's DetailDrawer onto the real Proposal model;
// the design's "via Claude" tag is dropped (the model has no Claude-origin
// flag) in favour of the resolved employee owner.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  CreativePreview,
  Proposal,
} from '../../../lib/api/proposals/proposals-api';
import { formatDurationMs } from './BatchCard';
import { proposalRiskLabel, proposalStatusLabel } from './proposalBadges';
import { proposalExpiryLine } from './proposalExpiry';
import {
  isCreativeProposal,
  replicationTargetsFor,
  replicationPlatformLabel,
  platformsToReplicate,
} from './replication';
import {
  PlatformMark,
  ReportMark,
  RequesterAvatar,
  Glyph,
  FailureReason,
  ProposalMarkers,
  platformMeta,
  proposalActionTitle,
  proposalEntity,
  proposalFailure,
  shopLabel,
  humanizeValue,
  changeMetric,
  countNoun,
  titleCase,
  deltaTone,
  formatPct,
  isClientReportProposal,
  clientReportMeta,
  reportTitle,
  reportReviewPath,
  isCurrentValueUnknown,
  UnknownCurrentValue,
} from './proposalFormat';

const LEAVE_MS = 180;
const MIN_DENY_REASON = 5;

// ---------- Phase 4: creative preview section ----------

// Copy fields we surface from `proposed_value.creative` (and per-card for
// carousels), in display order. Anything absent is simply skipped; budget /
// status proposals keep the existing humanized change block untouched.
const CREATIVE_COPY_FIELDS: Array<{ key: string; labelKey: string }> = [
  { key: 'message', labelKey: 'CreativeMessage' },
  { key: 'title', labelKey: 'CreativeTitle' },
  { key: 'headline', labelKey: 'CreativeTitle' },
  { key: 'description', labelKey: 'Description' },
  { key: 'call_to_action', labelKey: 'CreativeCallToAction' },
];

function creativeCopyRows(
  source: Record<string, any> | null | undefined,
  t: (key: string) => string
): Array<{ label: string; value: string }> {
  if (!source || typeof source !== 'object') return [];
  const rows: Array<{ label: string; value: string }> = [];
  const seenLabels = new Set<string>();
  for (const field of CREATIVE_COPY_FIELDS) {
    const raw = source[field.key];
    if (raw == null || raw === '') continue;
    const label = t(field.labelKey);
    if (seenLabels.has(label)) continue; // title vs headline alias
    seenLabels.add(label);
    // call_to_action may be an object ({ type: 'SHOP_NOW', ... }) or a string.
    const value =
      typeof raw === 'object'
        ? String(raw.type ?? raw.value ?? JSON.stringify(raw))
        : String(raw);
    rows.push({ label, value: value.replace(/_/g, ' ') });
  }
  return rows;
}

function previewMetaLine(preview: CreativePreview): string {
  const parts: string[] = [];
  if (preview.width && preview.height) {
    parts.push(`${preview.width}×${preview.height}`);
  }
  if (preview.aspect_ratio) parts.push(preview.aspect_ratio);
  if (preview.kind === 'video' && preview.duration_ms != null) {
    parts.push(formatDurationMs(preview.duration_ms));
  }
  return parts.join(' · ');
}

function CreativeSection({ proposal }: { proposal: Proposal }) {
  const { t } = useTranslation();
  const previews = proposal.creative_previews ?? [];
  if (previews.length === 0) return null;

  const creative =
    (proposal.proposed_value?.creative as Record<string, any> | undefined) ||
    undefined;
  const topCopy = creativeCopyRows(creative, t);
  const cards: Array<Record<string, any>> = Array.isArray(creative?.cards)
    ? creative!.cards
    : [];

  return (
    <section className='aq-dsec'>
      <h3 className='aq-dsec__label'>{t('Creative')}</h3>
      <div className='aq-creative'>
        <div className='aq-creative__thumbs'>
          {previews.map((preview) => (
            <figure key={preview.asset_id} className='aq-creative__fig'>
              {preview.kind === 'image' && preview.preview_url ? (
                <img
                  className='aq-creative__img'
                  src={preview.preview_url}
                  alt={t('Creative')}
                  loading='lazy'
                />
              ) : (
                <span className='aq-creative__ph'>
                  <Glyph
                    name={preview.kind === 'video' ? 'play' : 'image'}
                    size={22}
                  />
                </span>
              )}
              <figcaption className='aq-creative__meta'>
                {previewMetaLine(preview) || preview.mime_type}
              </figcaption>
            </figure>
          ))}
        </div>

        {topCopy.length > 0 && (
          <dl className='aq-creative__copy'>
            {topCopy.map((row) => (
              <div key={row.label} className='aq-creative__copy-row'>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        )}

        {cards.length > 0 && (
          <div className='aq-creative__cards'>
            {cards.map((card, i) => {
              const cardCopy = creativeCopyRows(card, t);
              if (cardCopy.length === 0) return null;
              return (
                <div key={i} className='aq-creative__card'>
                  <span className='aq-creative__card-label'>
                    {t('CreativeCard')} {i + 1}
                  </span>
                  <dl className='aq-creative__copy'>
                    {cardCopy.map((row) => (
                      <div key={row.label} className='aq-creative__copy-row'>
                        <dt>{row.label}</dt>
                        <dd>{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              );
            })}
          </div>
        )}

        {/* Phase 4 — passive replication hint for already-approved creatives.
            Approve-time replication is the acted path; no action button here
            in v1 (scope kept tight). */}
        {REPLICATION_HINT_STATUSES.has(proposal.status) && (
          <p className='aq-creative__hint'>
            <Glyph name='clock' size={13} /> {t('ReplicationDrawerHint')}
          </p>
        )}
      </div>
    </section>
  );
}

// Statuses past the approve gate — the point where approve-time replication
// is no longer offered for this proposal.
const REPLICATION_HINT_STATUSES = new Set<Proposal['status']>([
  'approved',
  'executing',
  'completed',
]);

interface ProposalDrawerProps {
  proposal: Proposal;
  requesterName?: string;
  canAct: boolean;
  initialDenyMode?: boolean;
  submitting?: boolean;
  onClose: () => void;
  // Phase 4 — `replicatePlatforms` carries the "also launch on" ticks made
  // in the drawer footer (creative proposals only; empty/omitted otherwise).
  onApprove: (
    proposal: Proposal,
    notes?: string,
    replicatePlatforms?: string[]
  ) => void;
  onDeny: (proposal: Proposal, reason: string) => void;
}

const ProposalDrawer = ({
  proposal,
  requesterName,
  canAct,
  initialDenyMode = false,
  submitting = false,
  onClose,
  onApprove,
  onDeny,
}: ProposalDrawerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Change 2 — client-report review proposals render a report-specific body
  // (brand / period / version + a "View report" deep-link) instead of the
  // budget-change block, and relabel the decision buttons.
  const isReport = isClientReportProposal(proposal);
  const rmeta = clientReportMeta(proposal);
  const reportPath = reportReviewPath(proposal);

  const [denyMode, setDenyMode] = useState(initialDenyMode);
  const [denyReason, setDenyReason] = useState('');
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  // Phase 4 — "also launch on" ticks. The drawer approve fires without a
  // confirm dialog, so the checkboxes live inline in the footer, right above
  // the approve button, and approve picks them up. Default unticked.
  const [replicateTargets, setReplicateTargets] = useState<Set<string>>(
    () => new Set()
  );
  const [entering, setEntering] = useState(true);
  const [leaving, setLeaving] = useState(false);
  const closeTimer = useRef<number | null>(null);

  // Reset the action surface whenever a different proposal is shown (the page
  // keeps one drawer instance mounted and swaps the proposal in).
  useEffect(() => {
    setDenyMode(initialDenyMode);
    setDenyReason('');
    setNote('');
    setShowNote(false);
    setReplicateTargets(new Set());
  }, [proposal.id, initialDenyMode]);

  const toggleReplicateTarget = (key: string) => {
    setReplicateTargets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Mount in the resting (off-screen) state, then drop `is-entering` on the
  // next frame so the CSS transition slides it in.
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntering(false));
    return () => cancelAnimationFrame(id);
  }, []);

  // Lock background scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const close = useCallback(() => {
    if (closeTimer.current) return; // already animating out
    setLeaving(true);
    closeTimer.current = window.setTimeout(onClose, LEAVE_MS);
  }, [onClose]);

  useEffect(
    () => () => {
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    },
    []
  );

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);

  const meta = platformMeta(proposal.platform);
  const title = isReport ? reportTitle(proposal) : proposalActionTitle(proposal, t);
  const curObj = proposal.current_value || {};
  const nextObj = proposal.proposed_value || {};
  const cur = humanizeValue(curObj);
  const next = humanizeValue(nextObj);
  const pct = proposal.change_percentage;
  const tone = deltaTone(proposal);
  const isCount = curObj.count !== undefined || nextObj.count !== undefined;
  const noun = countNoun(proposal);
  const metric = isCount && noun ? titleCase(noun) : changeMetric(nextObj);
  const entity = proposalEntity(proposal);
  const failure = proposalFailure(proposal);

  const stateClass = leaving ? 'is-leaving' : entering ? 'is-entering' : '';
  const denyValid = denyReason.trim().length >= MIN_DENY_REASON;

  // Phase 4 — replicate targets offered in the footer: creative proposals
  // only, the 5 canonical platforms minus this proposal's own.
  const replTargets = isCreativeProposal(proposal)
    ? replicationTargetsFor(proposal)
    : [];

  // Only a proposal that still waits, and that carries an expiry, has one.
  const expiryLine = proposalExpiryLine(t, proposal);

  const submitDeny = () => {
    if (denyValid && !submitting) onDeny(proposal, denyReason.trim());
  };

  return (
    <>
      <div
        className={`aq-drawer__scrim ${stateClass}`}
        onClick={close}
        aria-hidden='true'
      />
      <aside
        className={`aq-drawer ${stateClass}`}
        role='dialog'
        aria-modal='true'
        aria-label={title}
      >
        <header className='aq-drawer__head'>
          <div className='aq-drawer__head-left'>
            {isReport ? (
              <ReportMark size={40} />
            ) : (
              <PlatformMark platform={proposal.platform} size={40} />
            )}
            <div>
              <h2 className='aq-drawer__title'>{title}</h2>
              <p className='aq-drawer__platname'>
                {isReport ? t('ClientReportReview') : meta.name}
              </p>
            </div>
          </div>
          <button
            type='button'
            className='aq-drawer__close'
            onClick={close}
            title={t('Close')}
            aria-label={t('Close')}
          >
            <Glyph name='x' size={18} />
          </button>
        </header>

        <div className='aq-drawer__badges'>
          <span className={`status-badge status-badge--${proposal.status}`}>
            {proposalStatusLabel(t, proposal.status)}
          </span>
          <span className={`risk-badge risk-badge--${proposal.risk_level}`}>
            {proposalRiskLabel(t, proposal.risk_level)}
          </span>
          <ProposalMarkers proposal={proposal} t={t} showFailed={false} />
        </div>

        <div className='aq-drawer__body'>
          {isReport ? (
            <section className='aq-dsec'>
              <div className='aq-dfield'>
                <span className='aq-dfield__k'>{t('Brand')}</span>
                <span className='aq-dfield__v'>
                  {rmeta.brand || shopLabel(proposal)}
                </span>
              </div>
              {rmeta.period_label && (
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('Period')}</span>
                  <span className='aq-dfield__v'>{rmeta.period_label}</span>
                </div>
              )}
              {rmeta.report_version != null && (
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('Version')}</span>
                  <span className='aq-dfield__v'>v{rmeta.report_version}</span>
                </div>
              )}
              {rmeta.created_by_type && (
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('Author')}</span>
                  <span className='aq-dfield__v'>
                    {rmeta.created_by_type === 'ai'
                      ? t('AIGenerated')
                      : t('EmployeeAuthored')}
                  </span>
                </div>
              )}
              {reportPath && (
                <button
                  type='button'
                  className='aq-btn aq-btn--approve'
                  style={{ marginTop: '0.9rem' }}
                  onClick={() => navigate(reportPath)}
                >
                  <Glyph name='externalLink' size={16} stroke={2} />{' '}
                  {t('ViewReport')}
                </button>
              )}
            </section>
          ) : (
            <>
              <section className='aq-dsec'>
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('Account')}</span>
                  <span className='aq-dfield__v'>{shopLabel(proposal)}</span>
                </div>
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('CampaignOrSet')}</span>
                  <span className='aq-dfield__v'>
                    {entity.primary || '—'}
                  </span>
                </div>
                <div className='aq-dfield'>
                  <span className='aq-dfield__k'>{t('EntityID')}</span>
                  <span className='aq-dfield__v aq-dfield__v--mono'>
                    {proposal.entity_id}
                  </span>
                </div>
              </section>

              <section className='aq-dsec'>
                <h3 className='aq-dsec__label'>{t('TheChange')}</h3>
                <div className='aq-change-block'>
                  <div className='aq-change-block__metric'>{metric}</div>
                  <div className='aq-change-block__values'>
                    <div className='aq-cb-val'>
                      <span className='aq-cb-val__label'>{t('Current')}</span>
                      {isCurrentValueUnknown(proposal) ? (
                        <UnknownCurrentValue size='lg' />
                      ) : (
                        <span className='aq-cb-val__num'>
                          {cur.text}
                          {cur.unit ? <em>{cur.unit}</em> : null}
                        </span>
                      )}
                    </div>
                    <Glyph
                      name='arrowRight'
                      size={20}
                      className='aq-change-block__arrow'
                    />
                    <div className='aq-cb-val'>
                      <span className='aq-cb-val__label'>{t('Proposed')}</span>
                      <span
                        className={`aq-cb-val__num aq-cb-val__num--${tone}`}
                      >
                        {next.text}
                        {next.unit ? <em>{next.unit}</em> : null}
                      </span>
                    </div>
                    {pct != null && (
                      <span className={`aq-cb-pct aq-cb-pct--${tone}`}>
                        {formatPct(pct)}
                      </span>
                    )}
                  </div>
                </div>
              </section>

              {/* Phase 4 — creative launch proposals carry signed previews +
                  proposed copy; budget/status proposals skip this entirely. */}
              <CreativeSection proposal={proposal} />
            </>
          )}

          <section className='aq-dsec'>
            <h3 className='aq-dsec__label'>
              {isReport ? t('Summary') : t('Why')}
            </h3>
            <p className='aq-dsec__reasoning'>{proposal.reasoning}</p>
          </section>

          <section className='aq-dsec'>
            <h3 className='aq-dsec__label'>{t('ProposedBy')}</h3>
            <div className='aq-reqline'>
              <span className='aq-req'>
                <RequesterAvatar name={requesterName} size={26} />
                <span className='aq-req__name'>
                  {requesterName || t('Teammate')}
                </span>
              </span>
              <span className='aq-reqline__sep' />
              <span className='aq-reqline__plat'>
                <PlatformMark platform={proposal.platform} size={22} />
                {meta.name}
              </span>
              <span className='aq-dsec__time'>
                <Glyph name='clock' size={13} /> {timeAgoLabel(proposal, t)}
              </span>
            </div>
            {expiryLine && (
              <p className='aq-dsec__expires' data-testid='aq-drawer-expires'>
                {expiryLine}
              </p>
            )}
          </section>

          {failure && (
            <section className='aq-dsec'>
              <h3 className='aq-dsec__label'>{t('ProposalFailureReason')}</h3>
              <p className='aq-dsec__reasoning'>
                <FailureReason
                  key={proposal.id}
                  reason={failure.reason}
                  t={t}
                />
              </p>
            </section>
          )}

          {proposal.status === 'denied' && proposal.review_notes && (
            <section className='aq-dsec'>
              <h3 className='aq-dsec__label'>{t('DenialReason')}</h3>
              <p className='aq-dsec__reasoning'>{proposal.review_notes}</p>
            </section>
          )}
        </div>

        {canAct && (
          <footer className='aq-drawer__foot'>
            {denyMode ? (
              <div className='aq-deny-form'>
                <input
                  className='aq-deny-form__input'
                  placeholder={
                    isReport
                      ? t('RequestChangesPlaceholder')
                      : t('DenialReasonRequired')
                  }
                  value={denyReason}
                  autoFocus
                  disabled={submitting}
                  onChange={(e) => setDenyReason(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitDeny();
                  }}
                />
                <div className='aq-deny-form__row'>
                  <button
                    type='button'
                    className='aq-btn aq-btn--ghost'
                    onClick={() => setDenyMode(false)}
                    disabled={submitting}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type='button'
                    className='aq-btn aq-btn--deny'
                    disabled={!denyValid || submitting}
                    onClick={submitDeny}
                  >
                    <Glyph name='x' size={16} stroke={2.1} />{' '}
                    {isReport ? t('RequestChanges') : t('ConfirmDeny')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Phase 4 — inline "also launch on" ticks for creative
                    proposals, right above the approve button; the previews
                    sit in the Creative section of the body above. */}
                {replTargets.length > 0 && (
                  <div className='aq-replicate aq-replicate--drawer'>
                    <p className='aq-replicate__label'>{t('AlsoLaunchOn')}</p>
                    <div className='aq-replicate__row'>
                      {replTargets.map((key) => (
                        <label key={key} className='aq-replicate__opt'>
                          <input
                            type='checkbox'
                            checked={replicateTargets.has(key)}
                            disabled={submitting}
                            onChange={() => toggleReplicateTarget(key)}
                          />
                          <span>{replicationPlatformLabel(key)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {showNote && (
                  <input
                    className='aq-note-input'
                    placeholder={t('OptionalApprovalNotes')}
                    value={note}
                    disabled={submitting}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
                <div className='aq-drawer__foot-row'>
                  <button
                    type='button'
                    className='aq-btn aq-btn--deny'
                    onClick={() => setDenyMode(true)}
                    disabled={submitting}
                  >
                    <Glyph name='x' size={16} stroke={2.1} />{' '}
                    {isReport ? t('RequestChanges') : t('Deny')}
                  </button>
                  <button
                    type='button'
                    className='aq-btn aq-btn--approve'
                    onClick={() =>
                      onApprove(
                        proposal,
                        note.trim() || undefined,
                        platformsToReplicate(proposal, replicateTargets)
                      )
                    }
                    disabled={submitting}
                  >
                    <Glyph name='check' size={16} stroke={2.1} />{' '}
                    {isReport ? t('ApproveAndPublish') : t('Approve')}
                  </button>
                </div>
                {!showNote && (
                  <button
                    type='button'
                    className='aq-link-btn'
                    onClick={() => setShowNote(true)}
                    disabled={submitting}
                  >
                    + {t('AddNote')}
                  </button>
                )}
              </>
            )}
          </footer>
        )}
      </aside>
    </>
  );
};

// Localized "<age> ago" for the proposed-by line. Kept inline so the atom-level
// timeAgo can stay i18n-free (it's reused in dense rows where "ago" is noise).
function timeAgoLabel(proposal: Proposal, t: (key: string) => string): string {
  const ts = new Date(proposal.created_at).getTime();
  if (Number.isNaN(ts)) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return t('JustNow');
  if (m < 60) return `${m}m ${t('Ago')}`;
  if (h < 24) return `${h}h ${t('Ago')}`;
  if (d < 7) return `${d}d ${t('Ago')}`;
  return new Date(proposal.created_at).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default ProposalDrawer;
